import React, { useMemo, useCallback } from 'react'
import { ICellRendererParams, ColDef } from 'ag-grid-enterprise'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { MessageSquare } from 'lucide-react'
import { FunctionOutlined } from '@ant-design/icons'
import { Popover, theme } from 'antd'
import { EnhancedStatementRow } from '../../../utils/dataEnhancement'
import { FormulaCalculationTooltip } from './FormulaCalculationTooltip'
import { useModalActions } from '../ModalContext'
import { revenueRowIdSelector } from '../../../../../redux/config/financialStatement/selectors'
import { useFormulaCalculation } from '../../../hooks/useFormulaCalculation'
import { GridContext } from '../../../../../shared/hooks/useGridContext'
import { StatementColumnContext } from '../../../hooks/useIncomeStatementColumns'

const { useToken } = theme

interface ValueCellRendererParams extends ICellRendererParams<EnhancedStatementRow> {
  isEdited?: boolean
  hasComments: boolean
  context: GridContext
}

const IconButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);

  /* Override global .lucide styles */
  svg {
    width: 12px !important;
    height: 12px !important;
  }

  &:hover {
    color: #1890ff;
  }
`

const FormulaIcon = styled(IconButton)<{ $color: string }>`
  opacity: 0.6;
  transition: opacity 0.2s ease;
  color: ${props => props.$color};

  &:hover {
    opacity: 1;
  }
`

const CellContainer = styled.div<{ $isEdited?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;
  height: 100%;
  width: 100%;
  position: relative;

  ${props =>
    props.$isEdited &&
    `
    background-color: rgba(255, 193, 7, 0.2);
  `}
`

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  position: absolute;
  left: -2px;
  top: 8px;
  transform: translateY(-50%);
  z-index: 1;
`

const ValueSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 1;
  gap: 2px;
  min-width: 0;
`

const PercentageText = styled.div`
  font-size: 0.9em;
  color: #999;
  height: 14px;
  line-height: 14px;
  text-align: right;
  width: 100%;
`

const ValueText = styled.div`
  height: 20px;
  line-height: 20px;
  text-align: right !important;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ValueCellRenderer: React.FC<ValueCellRendererParams> = ({
  value,
  valueFormatted,
  column,
  colDef,
  api,
  data,
  isEdited,
  hasComments,
  context
}) => {
  const { t } = useTranslation()
  const { openCommentModal } = useModalActions()
  const revenueRowId = useSelector(revenueRowIdSelector)
  const { token } = useToken()

  // Extract column context to get statement column data (not grid context)
  const colDefWithContext = colDef as ColDef<EnhancedStatementRow> & { context?: StatementColumnContext }
  const statementColumn = colDefWithContext.context?.column

  // Use formula calculation hook
  const { formulaStatus, periodInfo } = useFormulaCalculation({
    data,
    statementColumn
  })

  // Get color for formula icon based on status
  const formulaIconColor = useMemo(() => {
    switch (formulaStatus) {
      case 'complete':
        return token.colorSuccess // Green - all drivers have values
      case 'partial':
        return token.colorWarning // Orange - some drivers missing
      case 'none':
        return token.colorError // Red - no drivers have values
      case 'fiscalYear':
        return token.colorTextSecondary // Gray - fiscal year level, no status
      default:
        return token.colorTextSecondary
    }
  }, [formulaStatus, token])

  // Get revenue row value (liikevaihto row) from config
  const revenueRowValue = useMemo(() => {
    if (!api || !column || !revenueRowId) return null

    // Find the revenue row - grid uses "HEADING-{id}" format for row keys
    // First try direct lookup with HEADING prefix
    let revenueNode = api.getRowNode(`HEADING-${revenueRowId}`)

    // Fallback: search through all rows if HEADING prefix doesn't work
    if (!revenueNode) {
      api.forEachNode(node => {
        if (node.data?.id === revenueRowId) {
          revenueNode = node
        }
      })
    }

    // Get the value using the column's valueGetter
    const columnDef = column.getColDef()
    if (!revenueNode || !revenueNode.data) return null
    if (columnDef.valueGetter && typeof columnDef.valueGetter === 'function') {
      return columnDef.valueGetter({
        data: revenueNode.data,
        node: revenueNode,
        colDef: columnDef,
        column,
        api,
        context: context || {}
      } as any)
    }

    return null
  }, [api, column, context, revenueRowId])

  // Calculate percentage
  const percentage = useMemo(() => {
    if (!revenueRowValue || revenueRowValue === 0 || !value) return null
    return ((value / revenueRowValue) * 100).toFixed(1)
  }, [value, revenueRowValue])

  const handleCommentClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation() // Prevent cell selection
      if (data && statementColumn) {
        openCommentModal({ row: data, column: statementColumn })
      }
    },
    [data, statementColumn, openCommentModal]
  )

  // Use valueFormatted from ag-Grid's valueFormatter
  // Don't show zero values
  const displayValue = useMemo(() => {
    if (value === undefined || value === null || value === 0) {
      return ''
    }
    return valueFormatted ?? ''
  }, [value, valueFormatted])

  // If no value (or zero) and no comments and not edited and no formula, don't render anything
  if ((value === undefined || value === null || value === 0) && !hasComments && !isEdited && !formulaStatus) {
    return null
  }

  return (
    <CellContainer $isEdited={isEdited}>
      {/* Icons container - absolute positioned on the left */}
      {(hasComments || (formulaStatus && data)) && (
        <IconsContainer>
          {/* Comment icon */}
          {hasComments && (
            <IconButton onClick={handleCommentClick}>
              <MessageSquare />
            </IconButton>
          )}

          {/* Formula calculation icon with tooltip - color coded by status */}
          {/* Monthly columns: full tooltip with driver values */}
          {context.isEditMode && formulaStatus && formulaStatus !== 'fiscalYear' && periodInfo && data && (
            <Popover
              content={
                <FormulaCalculationTooltip
                  formula={data.budgetingMethod!.source!}
                  periodYear={periodInfo.year}
                  periodMonth={periodInfo.month}
                  dimensionId={data.dimensionId || null}
                />
              }
              title={t('financialStatementsPage:driverCalculation')}
              trigger="click"
              placement="bottom"
            >
              <FormulaIcon $color={formulaIconColor}>
                <FunctionOutlined />
              </FormulaIcon>
            </Popover>
          )}

          {/* Fiscal year columns: simple tooltip with just formula */}
          {context.isEditMode && formulaStatus === 'fiscalYear' && data && (
            <Popover
              content={<FormulaCalculationTooltip formula={data.budgetingMethod!.source!} fiscalYearMode />}
              title={t('financialStatementsPage:formula')}
              trigger="click"
              placement="bottom"
            >
              <FormulaIcon $color={formulaIconColor}>
                <FunctionOutlined />
              </FormulaIcon>
            </Popover>
          )}
        </IconsContainer>
      )}

      {/* Value on the right */}
      <ValueSection>
        {context?.showPercentage && percentage && <PercentageText>{percentage}%</PercentageText>}
        <ValueText>{displayValue}</ValueText>
      </ValueSection>
    </CellContainer>
  )
}

export default ValueCellRenderer
